import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { getInvoices, deleteInvoice, setInvoicePaid } from "../WriteToDb";
import { useNotifications } from "../components/NotificationCenter";

const InvoiceList = ({
  onCreate,
  onEdit,
  onUpgrade,
  planLimits,
  invoiceUsage = 0,
}) => {
  const { notify } = useNotifications();
  const [invoices, setInvoices] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const [unpaidOpen, setUnpaidOpen] = useState(true);
  const [paidOpen, setPaidOpen] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchMode, setSearchMode] = useState("all");
  const [showUpgradeModal, setShowUpgradeModal] = useState(
    planLimits?.planId === "free"
  );

  const invoiceLimit = planLimits?.invoiceLimit ?? Infinity;
  const limitedPlan = Number.isFinite(invoiceLimit);
  const usedInvoices = Number.isFinite(invoiceUsage) ? invoiceUsage : 0;
  const remainingInvoices = limitedPlan
    ? Math.max(invoiceLimit - usedInvoices, 0)
    : null;
  const limitReached = limitedPlan && remainingInvoices === 0;

  useEffect(() => {
    if (planLimits?.planId !== "free") {
      setShowUpgradeModal(false);
    }
  }, [planLimits?.planId]);

  const fetchInvoices = useCallback(async () => {
    try {
      const rows = await getInvoices();

      const mapped = (rows ?? []).map((r) => {
        const date = r.invoiceDate ?? r.createdAt;
        const total = Number(r.total ?? 0);

        const items = r.items ?? [];

        const jobNames = Array.from(
          new Set(
            items.map((it) => it.jobName?.trim()).filter((name) => !!name)
          )
        );

        return {
          id: String(r.id),
          invoiceNumber: r.invoiceNumber,
          customerName: r.customerName,
          invoiceDate: date,
          total,
          formattedDate: date ? dayjs(date).format("DD/MM/YYYY") : "",
          formattedTotal: `£${total.toFixed(2)}`,
          isPaid: r.isPaid,
          jobNames,
        };
      });

      mapped.sort((a, b) =>
        (b.invoiceDate || "").localeCompare(a.invoiceDate || "")
      );

      setInvoices(mapped);
    } catch (e) {
      console.warn("Failed to load invoices", e);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchInvoices();
    setRefreshing(false);
  }, [fetchInvoices]);

  const confirmDelete = useCallback(
    (inv) => {
      if (!window.confirm(`Delete invoice #${inv.invoiceNumber}?`)) return;

      (async () => {
        try {
          setInvoices((prev) => prev.filter((x) => x.id !== inv.id));
          await deleteInvoice(inv.id);
        } catch (e) {
          console.warn("Delete failed", e);
          notify({
            type: "error",
            title: "Delete failed",
            message: "Could not delete the invoice. Please try again.",
          });
          fetchInvoices();
        }
      })();
    },
    [fetchInvoices, notify]
  );

  const togglePaid = useCallback(
    async (inv) => {
      const nextPaid = !inv.isPaid;

      setInvoices((prev) =>
        prev.map((x) => (x.id === inv.id ? { ...x, isPaid: nextPaid } : x))
      );

      try {
        await setInvoicePaid(Number(inv.id), nextPaid);
      } catch (e) {
        console.warn("Failed to update paid status", e);
        notify({
          type: "error",
          title: "Update failed",
          message: "Could not update paid status. Try again.",
        });

        setInvoices((prev) =>
          prev.map((x) => (x.id === inv.id ? { ...x, isPaid: inv.isPaid } : x))
        );
      }
    },
    [notify]
  );

  const goToInvoiceGenerator = (id) => {
    if (typeof window === "undefined") return;
    const base = "/invoice-generator";
    window.location.href = id ? `${base}/${id}` : base;
  };

  const searchPlaceholder = useMemo(() => {
    switch (searchMode) {
      case "customer":
        return "Search customer…";
      case "job":
        return "Search job name…";
      case "price":
        return "Search by amount…";
      case "invoice":
        return "Search invoice number…";
      case "all":
      default:
        return "Search invoices…";
    }
  }, [searchMode]);

  const filteredInvoices = useMemo(() => {
    const raw = searchQuery.trim();
    if (!raw) return invoices;

    const q = raw.toLowerCase();

    const matchesInvoiceNumber = (inv) =>
      inv.invoiceNumber?.toLowerCase().includes(q);

    const matchesCustomer = (inv) =>
      inv.customerName?.toLowerCase().includes(q);

    const matchesJob = (inv) => {
      if (!inv.jobNames || inv.jobNames.length === 0) return false;
      return inv.jobNames.some((name) => name.toLowerCase().includes(q));
    };

    const matchesPrice = (inv) => {
      const numeric = parseFloat(raw.replace(/[^\d.]/g, ""));
      if (!Number.isNaN(numeric)) {
        return (
          inv.total === numeric ||
          inv.total.toFixed(2).startsWith(numeric.toFixed(2))
        );
      }
      return inv.formattedTotal.toLowerCase().includes(q);
    };

    return invoices.filter((inv) => {
      switch (searchMode) {
        case "customer":
          return matchesCustomer(inv);
        case "job":
          return matchesJob(inv);
        case "price":
          return matchesPrice(inv);
        case "invoice":
          return matchesInvoiceNumber(inv);
        case "all":
        default:
          return (
            matchesCustomer(inv) ||
            matchesInvoiceNumber(inv) ||
            matchesJob(inv) ||
            matchesPrice(inv)
          );
      }
    });
  }, [invoices, searchQuery, searchMode]);

  const unpaid = useMemo(
    () => filteredInvoices.filter((i) => !i.isPaid),
    [filteredInvoices]
  );
  const paid = useMemo(
    () => filteredInvoices.filter((i) => i.isPaid),
    [filteredInvoices]
  );

  const sections = useMemo(
    () => [
      {
        key: "unpaid",
        title: "Unpaid",
        open: unpaidOpen,
        count: unpaid.length,
        data: unpaidOpen ? unpaid : [],
      },
      {
        key: "paid",
        title: "Paid",
        open: paidOpen,
        count: paid.length,
        data: paidOpen ? paid : [],
      },
    ],
    [unpaidOpen, paidOpen, unpaid, paid]
  );

  const filterOptions = [
    { mode: "all", label: "All" },
    { mode: "customer", label: "Customer" },
    { mode: "job", label: "Job" },
    { mode: "price", label: "Price" },
    { mode: "invoice", label: "Invoice #" },
  ];

  const closeUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false);
  }, []);

  const handleUpgradeClick = useCallback(() => {
    if (typeof onUpgrade === "function") {
      onUpgrade();
    }
    setShowUpgradeModal(false);
  }, [onUpgrade]);

  const handleCreateClick = () => {
    if (limitReached) {
      notify({
        type: "warning",
        title: "Invoice limit reached",
        message:
          "You've used all invoices on your plan. Upgrade to Pro for unlimited invoices.",
      });
      if (typeof onUpgrade === "function") {
        onUpgrade();
      }
      return;
    }

    if (onCreate) {
      onCreate();
    } else {
      goToInvoiceGenerator();
    }
  };

  return (
    <>
      {showUpgradeModal && planLimits?.planId === "free" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-8">
          <div
            className="relative w-full max-w-md rounded-3xl border border-slate-100 bg-white p-6 text-center shadow-2xl"
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              aria-label="Close upgrade reminder"
              onClick={closeUpgradeModal}
              className="absolute right-3 top-3 rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            >
              ✕
            </button>
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 text-amber-600">
              <ion-icon name="sparkles-outline" aria-hidden="true"></ion-icon>
            </div>
            <h2 className="text-lg font-semibold text-slate-900">
              Upgrade your subscription
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Upgrade to Pro to unlock unlimited invoices, saved customers, and
              detailed performance metrics.
            </p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={handleUpgradeClick}
                className="flex-1 rounded-full bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
              >
                View plans
              </button>
              <button
                type="button"
                onClick={closeUpgradeModal}
                className="flex-1 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {limitedPlan && (
          <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm">
            <div className="font-semibold">
              Free plan invoices remaining:{" "}
              {Math.max(remainingInvoices ?? 0, 0)} / {invoiceLimit}
            </div>
            {limitReached ? (
              <div className="mt-1 flex flex-wrap items-center gap-3">
                <span>You’ve reached the Free plan limit.</span>
                <button
                  type="button"
                  onClick={handleUpgradeClick}
                  className="rounded-full bg-black px-3 py-1 text-xs font-semibold text-white"
                >
                  Upgrade for more
                </button>
              </div>
            ) : (
              <div className="mt-1 text-xs text-amber-700">
                {Math.max(remainingInvoices ?? 0, 0)} invoice
                {remainingInvoices === 1 ? "" : "s"} remaining this month.
              </div>
            )}
          </div>
        )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Invoices</h1>
          <p className="text-sm text-slate-500">
            Review, filter and manage your invoices.
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 shadow-sm">
            <span className="mr-2 mt-1 text-slate-400">
              <ion-icon name="search-outline" aria-hidden="true"></ion-icon>
            </span>
            <input
              type="text"
              className="w-full border-none bg-transparent text-sm outline-none placeholder:text-slate-400"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery.length > 0 && (
              <button
                onClick={() => setSearchQuery("")}
                className="ml-1 text-xs text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            )}
          </div>

          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>

          <button
            onClick={handleCreateClick}
            disabled={limitReached}
            className="inline-flex items-center justify-center rounded-full bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {limitReached ? "Invoice limit reached" : "+ New invoice"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        {filterOptions.map((opt) => {
          const active = searchMode === opt.mode;
          return (
            <button
              key={opt.mode}
              onClick={() => setSearchMode(opt.mode)}
              className={[
                "rounded-full px-3 py-1 text-xs font-medium",
                active
                  ? "bg-slate-900 text-slate-50"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200",
              ].join(" ")}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      <div className="mt-2 space-y-4">
        {sections.map((section) => {
          const toggle =
            section.key === "unpaid"
              ? () => setUnpaidOpen((v) => !v)
              : () => setPaidOpen((v) => !v);

          return (
            <section
              key={section.key}
              className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm"
            >
              <button
                onClick={toggle}
                className="flex w-full items-center justify-between rounded-xl px-1 py-1 text-left hover:bg-slate-50"
              >
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">
                    {section.open ? "▾" : "▸"}
                  </span>
                  <span className="text-sm font-semibold text-slate-900">
                    {section.title} ({section.count})
                  </span>
                </div>
                <span className="text-xs text-slate-500">
                  {section.open ? "Click to collapse" : "Click to expand"}
                </span>
              </button>

              {section.open && (
                <div className="mt-2 divide-y divide-slate-100">
                  {section.data.length === 0 ? (
                    <p className="px-1 py-3 text-sm text-slate-500">
                      No {section.title.toLowerCase()} invoices found.
                    </p>
                  ) : (
                    section.data.map((inv) => (
                      <article
                        key={inv.id}
                        className="flex flex-col gap-2 px-2 py-3 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="flex-1 space-y-0.5">
                          <div className="flex items-center gap-2">
                            <h2 className="text-sm font-semibold text-slate-900">
                              {inv.customerName || "Unnamed customer"}
                            </h2>
                            <span
                              className={[
                                "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold",
                                inv.isPaid
                                  ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
                                  : "bg-amber-50 text-amber-700 ring-1 ring-amber-100",
                              ].join(" ")}
                            >
                              {inv.isPaid ? "Paid" : "Unpaid"}
                            </span>
                          </div>

                          {inv.jobNames && inv.jobNames.length > 0 && (
                            <p className="text-xs text-slate-500">
                              {(() => {
                                const names = inv.jobNames;
                                return names.length > 3
                                  ? `${names.slice(0, 2).join(" · ")} · …`
                                  : names.join(" · ");
                              })()}
                            </p>
                          )}

                          <p className="text-xs text-slate-500">
                            Invoice #{inv.invoiceNumber} ·{" "}
                            {inv.formattedDate || "No date"}
                          </p>
                        </div>

                        <div className="mt-1 flex flex-col items-start gap-1 sm:mt-0 sm:items-end">
                          <div className="text-right text-base font-semibold text-slate-900">
                            {inv.formattedTotal}
                          </div>

                          <div className="flex flex-wrap gap-1">
                            <button
                              onClick={() =>
                                onEdit
                                  ? onEdit({ id: inv.id })
                                  : goToInvoiceGenerator(inv.id)
                              }
                              className="rounded-full bg-slate-900 px-3 py-1 text-xs font-medium text-white hover:bg-slate-800"
                            >
                              Edit
                            </button>

                            <button
                              onClick={() => togglePaid(inv)}
                              className={[
                                "rounded-full px-3 py-1 text-xs font-medium",
                                inv.isPaid
                                  ? "bg-amber-50 text-amber-700 hover:bg-amber-100"
                                  : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
                              ].join(" ")}
                            >
                              {inv.isPaid ? "Mark unpaid" : "Mark paid"}
                            </button>

                            <button
                              onClick={() => confirmDelete(inv)}
                              className="rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-100"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </article>
                    ))
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>
      </div>
    </>
  );
};

export default InvoiceList;
